import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type {
  PurchaseOrderDto,
  PurchaseOrderSuggestionDto,
  ReceivingDocumentDto,
  CreateReceivingDocumentRequest,
  CancelReceivingDocumentRequest,
  PostReceivingDocumentRequest,
  ReceivingDocumentResyncResultDto,
  EditReceivedQuantityRequest,
} from '../types/receiving.types';
import { PostingStatusEnum } from '../types/receiving.types';
import { receivingService } from '../services/receivingService';
import { purchaseOrderService } from '@/features/purchase-orders/services/purchaseOrderService';
import { getApiErrorMessage, getApiErrorDetails } from '@/lib/getApiErrorMessage';
import { useActivityRefreshStore } from '@features/activity';
import {
  fetchOutboxStatus,
  fetchActiveOutboxStatuses,
  outboxKey,
  outboxType,
  groupOutboxByDocId,
  type OutboxPostingStatusDto,
} from '@shared/posting';

// 86d3mnuxe — the IR posting lifecycle moved off the ReceivingDocument entity onto the OUTBOX. getById no
// longer carries postingStatus (the column was dropped), so we overlay it from /api/outbox/status.
// netSuiteItemReceiptId is the source of truth for a successful post — a stale terminal-"Failed" job from an
// earlier attempt lingers in the projection (IR re-post enqueues a NEW job, DedupeKey null), so once the doc
// actually has a NetSuite id we ignore it. Otherwise: any in-flight job → Posting; else the most-recent
// Failed → PostingFailed; else None.
function applyIrPosting(doc: ReceivingDocumentDto, jobs: OutboxPostingStatusDto[]): ReceivingDocumentDto {
  if (doc.netSuiteItemReceiptId) {
    return { ...doc, postingStatus: PostingStatusEnum.None, postingFailedReason: undefined };
  }
  if (jobs.some((j) => j.status === 'Posting')) {
    return { ...doc, postingStatus: PostingStatusEnum.Posting, postingFailedReason: undefined };
  }
  const failed = jobs.filter((j) => j.status === 'Failed');
  if (failed.length > 0) {
    const last = failed[failed.length - 1];
    return {
      ...doc,
      postingStatus: PostingStatusEnum.PostingFailed,
      postingFailedReason: last.lastError ?? undefined,
      // 86d3mnuxe funnel — reach disclosure: the banner flips Retry → Reconcile (and hides Cancel)
      // when the failed attempt may have created an Item Receipt.
      postingReach: last.postReach,
      postingNetSuiteRecordId: last.netSuiteRecordId,
    };
  }
  return { ...doc, postingStatus: PostingStatusEnum.None, postingFailedReason: undefined };
}

// Document ids with a settle-poll currently running — dedupes a re-entrant poll (hydrate-on-open while the
// post's original poll is still looping).
const activePolls = new Set<number>();

interface ReceivingState {
  // All documents list (all statuses)
  allDocuments: ReceivingDocumentDto[];
  // Pending put-away list
  pendingDocuments: ReceivingDocumentDto[];
  isLoading: boolean;
  error: string | null;

  // Paginated PO list
  pagedPOs: PurchaseOrderDto[];
  poTotal: number;
  poPage: number;
  poPageSize: number;
  isLoadingPOs: boolean;
  poError: string | null;

  // Suggestion search (separate from paginated list)
  suggestions: PurchaseOrderSuggestionDto[];
  isLoadingSuggestions: boolean;

  // Legacy: full list (kept for compatibility, unused by new page)
  openPurchaseOrders: PurchaseOrderDto[];

  // Selected PO for receiving form
  selectedPO: PurchaseOrderDto | null;

  // Preview PO for detail panel (does NOT trigger navigation)
  selectedPreviewPO: PurchaseOrderDto | null;
  isLoadingPreview: boolean;

  // Detail
  selectedDocument: ReceivingDocumentDto | null;
  isLoadingDetail: boolean;
  detailError: string | null;

  // Submit state
  isSubmitting: boolean;
  submitError: string | null;
  submitFieldErrors: Record<string, string[]> | null;

  /**
   * Cross-user visibility toggle, persisted to localStorage. true = show all (the backend silently
   * downgrades when the caller lacks `receiving.view_all`), false = scope to documents this user received.
   *
   * ⚠️ It scopes LIST queries only. It used to be passed through every read call including `getById`, so
   * turning the toggle off 404'd every document the user had not received — a table filter deciding
   * whether a record could be opened. Detail reads now always request view-all and let the server decide,
   * which is what `EnsureCanAccessDocument` already does for the MUTATING endpoints, explicitly "so
   * supervisors keep cross-user access". The read path was the odd one out.
   * Same defect and same fix as the picklist toggle (2026-09-11).
   */
  viewAll: boolean;
  setViewAll: (viewAll: boolean) => void;

  // Actions
  fetchPendingDocuments: () => Promise<void>;
  fetchOpenPurchaseOrders: () => Promise<void>;
  fetchPagedPurchaseOrders: (page?: number, pageSize?: number) => Promise<void>;
  searchSuggestions: (query: string) => Promise<void>;
  clearSuggestions: () => void;
  fetchPurchaseOrderById: (netSuitePOId: string) => Promise<void>;
  selectPurchaseOrder: (po: PurchaseOrderDto | null) => void;
  setPreviewPO: (po: PurchaseOrderDto | null) => void;
  fetchPreviewPOById: (netSuitePOId: string) => Promise<void>;
  createReceivingDocument: (request: CreateReceivingDocumentRequest) => Promise<ReceivingDocumentDto>;
  fetchAllDocuments: () => Promise<void>;
  fetchDocumentById: (id: number) => Promise<void>;
  cancelDocument: (id: number, request?: CancelReceivingDocumentRequest) => Promise<ReceivingDocumentDto>;
  resyncDocument: (id: number) => Promise<ReceivingDocumentResyncResultDto>;
  postReceivingDocument: (id: number, request: PostReceivingDocumentRequest) => Promise<void>;
  /** 86d3mnuxe — background settle-poll: overlay outbox posting status onto the doc until it settles.
   *  Dedupes a re-entrant poll; safe to call from post + hydrate-on-open. */
  pollReceivingPosting: (id: number) => void;
  editReceivedQuantity: (docId: number, itemId: number, request: EditReceivedQuantityRequest) => Promise<ReceivingDocumentDto>;
  updateTimeOut: (id: number, timeOut: string) => Promise<ReceivingDocumentDto>;
  clearErrors: () => void;
}

export const useReceivingStore = create<ReceivingState>()(persist((set, get) => ({
  allDocuments: [],
  pendingDocuments: [],
  isLoading: false,
  error: null,

  pagedPOs: [],
  poTotal: 0,
  poPage: 1,
  poPageSize: 25,
  isLoadingPOs: false,
  poError: null,

  suggestions: [],
  isLoadingSuggestions: false,

  openPurchaseOrders: [],

  selectedPO: null,
  selectedPreviewPO: null,
  isLoadingPreview: false,

  selectedDocument: null,
  isLoadingDetail: false,
  detailError: null,

  isSubmitting: false,
  submitError: null,
  submitFieldErrors: null,

  // Cross-user visibility toggle (defaults to show-all on web)
  viewAll: true,
  setViewAll: (viewAll) => set({ viewAll }),

  fetchPendingDocuments: async () => {
    set({ isLoading: true, error: null });
    try {
      const documents = await receivingService.getPendingPutAway(get().viewAll);
      set({ pendingDocuments: documents, isLoading: false });
    } catch (error) {
      set({ error: getApiErrorMessage(error, 'Failed to fetch receiving documents'), isLoading: false });
    }
  },

  fetchOpenPurchaseOrders: async () => {
    set({ isLoadingPOs: true, poError: null });
    try {
      const orders = await purchaseOrderService.getAll();
      // Filter to receivable POs (Open status, not flagged)
      const open = orders.filter((po) => !po.forAdjustment && po.documentStatus === 1);
      set({ openPurchaseOrders: open, isLoadingPOs: false });
    } catch (error) {
      set({ poError: getApiErrorMessage(error, 'Failed to fetch purchase orders'), isLoadingPOs: false });
    }
  },

  fetchPagedPurchaseOrders: async (page?: number, pageSize?: number) => {
    const state = get();
    const resolvedPage = page ?? state.poPage;
    const resolvedPageSize = pageSize ?? state.poPageSize;
    set({ isLoadingPOs: true, poError: null, poPage: resolvedPage, poPageSize: resolvedPageSize });
    try {
      const allOrders = await purchaseOrderService.getAll();
      // Filter to receivable POs (Open status, not flagged)
      const open = allOrders.filter((po) => !po.forAdjustment && po.documentStatus === 1);
      const total = open.length;
      const start = (resolvedPage - 1) * resolvedPageSize;
      const paged = open.slice(start, start + resolvedPageSize);
      set({
        pagedPOs: paged,
        poTotal: total,
        poPage: resolvedPage,
        poPageSize: resolvedPageSize,
        isLoadingPOs: false,
      });
    } catch (error) {
      set({ poError: getApiErrorMessage(error, 'Failed to fetch purchase orders'), isLoadingPOs: false });
    }
  },

  searchSuggestions: async (query: string) => {
    if (!query.trim()) {
      set({ suggestions: [], isLoadingSuggestions: false });
      return;
    }
    set({ isLoadingSuggestions: true });
    try {
      const allOrders = await purchaseOrderService.getAll();
      const q = query.toLowerCase();
      const matches = allOrders
        .filter((po) => po.poNumber.toLowerCase().includes(q) || po.vendorName.toLowerCase().includes(q))
        .slice(0, 10)
        .map((po) => ({ netSuitePOId: String(po.id), poNumber: po.poNumber, vendorName: po.vendorName }));
      set({ suggestions: matches, isLoadingSuggestions: false });
    } catch {
      set({ suggestions: [], isLoadingSuggestions: false });
    }
  },

  clearSuggestions: () => set({ suggestions: [] }),

  fetchPurchaseOrderById: async (poId: string) => {
    set({ isLoadingPOs: true, poError: null, selectedPO: null });
    try {
      const id = parseInt(poId, 10);
      if (isNaN(id)) throw new Error('Invalid purchase order ID');
      const order = await purchaseOrderService.getById(id);
      set({ selectedPO: order, isLoadingPOs: false });
    } catch (error) {
      set({ poError: getApiErrorMessage(error, 'Failed to fetch purchase order'), isLoadingPOs: false });
    }
  },

  selectPurchaseOrder: (po) => set({ selectedPO: po }),

  setPreviewPO: (po) => set({ selectedPreviewPO: po }),

  fetchPreviewPOById: async (poId: string) => {
    set({ isLoadingPreview: true, poError: null });
    try {
      const id = parseInt(poId, 10);
      if (isNaN(id)) throw new Error('Invalid purchase order ID');
      const order = await purchaseOrderService.getById(id);
      set({ selectedPreviewPO: order, isLoadingPreview: false });
    } catch (error) {
      set({ poError: getApiErrorMessage(error, 'Failed to fetch purchase order'), isLoadingPreview: false });
    }
  },

  createReceivingDocument: async (request) => {
    set({ isSubmitting: true, submitError: null, submitFieldErrors: null });
    try {
      const document = await receivingService.createReceivingDocument(request);
      set((state) => ({
        pendingDocuments: [document, ...state.pendingDocuments],
        isSubmitting: false,
        selectedPO: null,
      }));
      useActivityRefreshStore.getState().bump();
      return document;
    } catch (error) {
      const { message, fieldErrors } = getApiErrorDetails(error, 'Failed to create receiving document');
      set({ submitError: message, submitFieldErrors: fieldErrors ?? null, isSubmitting: false });
      throw error;
    }
  },

  fetchAllDocuments: async () => {
    set({ isLoading: true, error: null });
    try {
      const documents = await receivingService.getAll(get().viewAll);
      set({ allDocuments: documents, isLoading: false });
      // 86d3mnuxe — overlay the outbox posting status onto every row (one bulk call), non-blocking so the
      // list renders immediately (perf gate). Drives the per-row Posting/Failed badge + the cross-session
      // auto-refresh loop (ReceivingDocumentsTab), since getAll no longer carries postingStatus.
      void (async () => {
        let jobs: OutboxPostingStatusDto[];
        try {
          jobs = await fetchActiveOutboxStatuses(outboxType.receiving);
        } catch {
          return;
        }
        // Nothing posting → the wholesale list fetch above already cleared status to None; skip the re-map
        // (avoids reference churn + an extra render in the common case).
        if (jobs.length === 0) return;
        const byId = groupOutboxByDocId(jobs);
        set((state) => ({
          allDocuments: state.allDocuments.map((d) => applyIrPosting(d, byId.get(d.id) ?? [])),
        }));
      })();
    } catch (error) {
      set({ error: getApiErrorMessage(error, 'Failed to fetch receiving documents'), isLoading: false });
    }
  },

  fetchDocumentById: async (id: number) => {
    set({ isLoadingDetail: true, detailError: null });
    try {
      const document = await receivingService.getById(id, true);
      set({ selectedDocument: document, isLoadingDetail: false });
      // 86d3mnuxe — overlay the outbox posting status without blocking the panel (perf gate): render now,
      // stream the posting card in. Covers a post started elsewhere / a page refresh mid-post, and resumes
      // the settle-poll for an in-flight post.
      void (async () => {
        // 86d3mnuxe BUG 2 — the OUTBOX IS READ FIRST, then the document is RE-fetched (the same
        // happens-before the settle-poll below relies on, 86d3peur0 #11): a settled job happens-after the
        // pipeline committed the NetSuite id, so a document fetched AFTER the outbox read is guaranteed to
        // carry it. Merging the initial render's document (fetched BEFORE the outbox) could pair a
        // pre-stamp doc with a post-settle empty projection → status None, no recovery poll → an
        // already-posted document stranded until a manual refresh.
        let jobs: OutboxPostingStatusDto[];
        let fresh: ReceivingDocumentDto;
        try {
          jobs = await fetchOutboxStatus(outboxKey.receiving(id));
          fresh = await receivingService.getById(id, true);
        } catch {
          return; // non-fatal — a running poll (if any) will catch up
        }
        const sel = get().selectedDocument;
        if (!sel || sel.id !== id) return; // navigated away
        const merged = applyIrPosting(fresh, jobs);
        set((state) => ({
          selectedDocument: state.selectedDocument?.id === id ? merged : state.selectedDocument,
          allDocuments: state.allDocuments.map((d) => (d.id === id ? merged : d)),
        }));
        if (merged.postingStatus === PostingStatusEnum.Posting) get().pollReceivingPosting(id);
      })();
    } catch (error) {
      set({ detailError: getApiErrorMessage(error, 'Failed to fetch receiving document'), isLoadingDetail: false });
    }
  },

  cancelDocument: async (id, request = {}) => {
    set({ isSubmitting: true, submitError: null, submitFieldErrors: null });
    try {
      const document = await receivingService.cancelDocument(id, request);
      set((state) => ({
        selectedDocument: document,
        allDocuments: state.allDocuments.map((d) => (d.id === id ? document : d)),
        pendingDocuments: state.pendingDocuments.filter((d) => d.id !== id),
        isSubmitting: false,
      }));
      useActivityRefreshStore.getState().bump();
      return document;
    } catch (error) {
      const { message, fieldErrors } = getApiErrorDetails(error, 'Failed to cancel receiving document');
      set({ submitError: message, submitFieldErrors: fieldErrors ?? null, isSubmitting: false });
      throw error;
    }
  },

  resyncDocument: async (id) => {
    set({ isSubmitting: true, submitError: null });
    try {
      const result = await receivingService.resyncDocument(id);
      // Refresh the document to get updated ForAdjustment flag
      const document = await receivingService.getById(id, true);
      set((state) => ({
        selectedDocument: document,
        allDocuments: state.allDocuments.map((d) => (d.id === id ? document : d)),
        pendingDocuments: state.pendingDocuments.map((d) => (d.id === id ? document : d)),
        isSubmitting: false,
      }));
      return result;
    } catch (error) {
      set({ submitError: getApiErrorMessage(error, 'Failed to resync receiving document'), isSubmitting: false });
      throw error;
    }
  },

  // 86d3kwtar — async posting. Claim (202), optimistically flip to Posting so the UI shows the
  // in-flight card immediately, then poll getById() until the worker settles PostingStatus. No SSE
  // for receiving — the notification service will replace this poll later.
  postReceivingDocument: async (id, request) => {
    set({ isSubmitting: true, submitError: null, submitFieldErrors: null });
    try {
      await receivingService.postReceivingDocument(id, request);
    } catch (error) {
      const { message, fieldErrors } = getApiErrorDetails(error, 'Failed to post Item Receipt');
      set({ submitError: message, submitFieldErrors: fieldErrors ?? null, isSubmitting: false });
      throw error;
    }

    const markPosting = (d: ReceivingDocumentDto): ReceivingDocumentDto =>
      ({ ...d, postingStatus: PostingStatusEnum.Posting, postingFailedReason: undefined });
    set((state) => ({
      selectedDocument: state.selectedDocument?.id === id ? markPosting(state.selectedDocument) : state.selectedDocument,
      allDocuments: state.allDocuments.map((d) => (d.id === id ? markPosting(d) : d)),
      isSubmitting: false,
    }));
    useActivityRefreshStore.getState().bump();

    // 86d3mnuxe — background settle-poll (outbox-sourced status). Fire-and-forget: the click already
    // returned; the posting card reflects the settle. Self-terminating + id-guarded + deduped.
    get().pollReceivingPosting(id);
  },

  pollReceivingPosting: (id: number) => {
    if (activePolls.has(id)) return; // a settle-poll for this document is already running
    activePolls.add(id);
    void (async () => {
      try {
        const start = Date.now();
        const POLL_MS = 3000;
        const MAX_MS = 5 * 60_000;
        while (Date.now() - start < MAX_MS) {
          await new Promise((r) => setTimeout(r, POLL_MS));
          // Posting status comes from the OUTBOX (getById no longer carries it). On a transient outbox blip
          // skip the tick so the optimistic/last-known status doesn't flicker to None.
          //
          // 86d3peur0 #11 — the OUTBOX IS READ FIRST, ON PURPOSE. A settled outbox (job Done) happens-after
          // the pipeline committed the Item Receipt id, so a document fetched afterwards is guaranteed to
          // carry it. The old order (document, then outbox) could read the doc just BEFORE the stamp and the
          // outbox just AFTER the job settled — yielding status None with a stale null netSuiteItemReceiptId,
          // which skipped the pending-list cleanup below and then returned, stranding an already-posted
          // document in the pending tab until a manual refresh. Do NOT collapse these into a Promise.all:
          // concurrent reads have no happens-before and reintroduce exactly that race.
          let jobs: OutboxPostingStatusDto[];
          try {
            jobs = await fetchOutboxStatus(outboxKey.receiving(id));
          } catch {
            continue;
          }
          let doc: ReceivingDocumentDto;
          try {
            doc = await receivingService.getById(id, true);
          } catch {
            continue; // transient — keep polling
          }
          const merged = applyIrPosting(doc, jobs);
          set((state) => ({
            selectedDocument: state.selectedDocument?.id === id ? merged : state.selectedDocument,
            allDocuments: state.allDocuments.map((d) => (d.id === id ? merged : d)),
          }));
          if (merged.postingStatus !== PostingStatusEnum.Posting) {
            if (merged.postingStatus === PostingStatusEnum.None && merged.netSuiteItemReceiptId) {
              set((state) => ({ pendingDocuments: state.pendingDocuments.filter((d) => d.id !== id) }));
              useActivityRefreshStore.getState().bump();
            }
            return;
          }
        }
      } finally {
        activePolls.delete(id);
      }
    })();
  },

  editReceivedQuantity: async (docId, itemId, request) => {
    set({ isSubmitting: true, submitError: null, submitFieldErrors: null });
    try {
      const document = await receivingService.editReceivedQuantity(docId, itemId, request);
      set((state) => ({
        selectedDocument: document,
        allDocuments: state.allDocuments.map((d) => (d.id === docId ? document : d)),
        pendingDocuments: state.pendingDocuments.map((d) => (d.id === docId ? document : d)),
        isSubmitting: false,
      }));
      useActivityRefreshStore.getState().bump();
      return document;
    } catch (error) {
      const { message, fieldErrors } = getApiErrorDetails(error, 'Failed to update received quantity');
      set({ submitError: message, submitFieldErrors: fieldErrors ?? null, isSubmitting: false });
      throw error;
    }
  },

  updateTimeOut: async (id, timeOut) => {
    set({ isSubmitting: true, submitError: null, submitFieldErrors: null });
    try {
      const document = await receivingService.updateTimeOut(id, timeOut);
      set((state) => ({
        selectedDocument: document,
        allDocuments: state.allDocuments.map((d) => (d.id === id ? document : d)),
        pendingDocuments: state.pendingDocuments.map((d) => (d.id === id ? document : d)),
        isSubmitting: false,
      }));
      useActivityRefreshStore.getState().bump();
      return document;
    } catch (error) {
      const { message, fieldErrors } = getApiErrorDetails(error, 'Failed to update Time Out');
      set({ submitError: message, submitFieldErrors: fieldErrors ?? null, isSubmitting: false });
      throw error;
    }
  },

  clearErrors: () =>
    set({ error: null, poError: null, detailError: null, submitError: null, submitFieldErrors: null }),
}), {
  name: 'csc-wms-receiving-prefs',
  storage: createJSONStorage(() => localStorage),
  // ONLY the scope preference. The rest is server data with a freshness contract; persisting a document
  // list would paint yesterday's quantities before any fetch lands.
  partialize: (state) => ({ viewAll: state.viewAll }),
}));
